import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';

const SIGNAL_VIDEO_SRC = '/media/clip-signal.mp4';
const SIGNAL_POSTER_SRC = '/media/signal-poster.webp';

const PILLARS = [
  {
    key: 'plan',
    index: '01',
    name: 'Plan',
    headline: 'Start studies on rails, not spreadsheets.',
    copy:
      'Startup pipelines, greenlight checklists, and reusable templates keep every new study moving — with calendars and staffing that already know who is out and who is covering.',
    items: [
      'Study startup pipelines & templates',
      'Milestones & greenlight checklists',
      'Study calendars & visit windows',
      'PTO- and delegation-aware coverage',
    ],
  },
  {
    key: 'execute',
    index: '02',
    name: 'Execute',
    headline: 'Run the clinic day without the scramble.',
    copy:
      'Check-in, encounters, eSource forms, and task worklists share one operational picture, so coordinators spend the day with patients instead of reconciling systems.',
    items: [
      'Visit scheduling & clinic check-in',
      'Tasks, worklists & queues',
      'Patient workflows & communications',
      'eSource forms & structured data capture',
    ],
  },
  {
    key: 'prove',
    index: '03',
    name: 'Prove',
    headline: 'Every action leaves evidence behind.',
    copy:
      'Documents, versions, signatures, monitoring, and QC reviews all write to the same audit trail — so the answer to “who did what, when” is always one lookup away.',
    items: [
      'Shared documents & version history',
      'Electronic signatures & review queues',
      'Monitor workspace, findings & queries',
      'Quality control & audit trails',
    ],
  },
  {
    key: 'pay',
    index: '04',
    name: 'Pay & Report',
    headline: 'Close the loop on money and metrics.',
    copy:
      'PhennxPay stipends, study finance, and reporting live beside the operational record — dashboards read from the work itself, not from a copy of it.',
    items: [
      'PhennxPay patient stipends',
      'Billing & study finance',
      'Operational & study reports',
      'Custom role-based dashboards',
    ],
  },
];

export default function PillarsSection() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);

  const showVideo = !prefersReducedMotion && !videoFailed;

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const section = sectionRef.current;
    if (!section) return undefined;

    let rafId = null;
    let lastIndex = -1;

    const update = () => {
      rafId = null;
      const rect = section.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      const index = Math.min(PILLARS.length - 1, Math.floor(progress * PILLARS.length));
      if (index !== lastIndex) {
        lastIndex = index;
        setActiveIndex(index);
      }
    };

    const handleScroll = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!showVideo) return;
    const video = videoRef.current;
    const playPromise = video?.play?.();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [showVideo]);

  const jumpToPillar = (index) => {
    const section = sectionRef.current;
    if (!section) return;
    const rect = section.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const sectionTop = window.scrollY + rect.top;
    const target = sectionTop + (total * (index + 0.5)) / PILLARS.length;
    window.scrollTo({ top: target, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  if (prefersReducedMotion) {
    return (
      <section className="hx-pillars hx-pillars--static" id="platform" aria-labelledby="platform-heading">
        <div className="hx-container">
          <p className="hx-eyebrow">
            <span className="hx-eyebrow__tick" aria-hidden="true" />
            The platform
          </p>
          <h2 className="hx-h2" id="platform-heading">
            Plan. Execute. Prove. Pay &amp; report.
          </h2>
          <div className="hx-pillars__static-grid">
            {PILLARS.map((pillar) => (
              <article className="hx-pillars__static-card" key={pillar.key}>
                <p className="hx-pillars__kicker">
                  {pillar.index} — {pillar.name}
                </p>
                <h3 className="hx-pillars__headline">{pillar.headline}</h3>
                <p className="hx-pillars__copy">{pillar.copy}</p>
                <ul className="hx-pillars__list">
                  {pillar.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="hx-pillars" id="platform" aria-labelledby="platform-heading">
      <div className="hx-pillars__stage">
        <div className="hx-pillars__media" aria-hidden="true">
          {showVideo ? (
            <video
              ref={videoRef}
              className="hx-pillars__video"
              src={SIGNAL_VIDEO_SRC}
              poster={SIGNAL_POSTER_SRC}
              muted
              playsInline
              loop
              autoPlay
              preload="metadata"
              tabIndex={-1}
              onError={() => setVideoFailed(true)}
            />
          ) : (
            <img className="hx-pillars__video" src={SIGNAL_POSTER_SRC} alt="" loading="lazy" />
          )}
          <div className="hx-pillars__scrim" />
        </div>

        <div className="hx-pillars__inner hx-container">
          <p className="hx-eyebrow">
            <span className="hx-eyebrow__tick" aria-hidden="true" />
            The platform
          </p>
          <h2 className="hx-h2 hx-pillars__title" id="platform-heading">
            One operational spine,
            <br /> four disciplines.
          </h2>

          <div className="hx-pillars__panels">
            {PILLARS.map((pillar, index) => (
              <article
                className={`hx-pillars__panel ${index === activeIndex ? 'is-active' : ''}`}
                key={pillar.key}
                aria-hidden={index !== activeIndex}
              >
                <p className="hx-pillars__kicker">
                  {pillar.index} — {pillar.name}
                </p>
                <h3 className="hx-pillars__headline">{pillar.headline}</h3>
                <p className="hx-pillars__copy">{pillar.copy}</p>
                <ul className="hx-pillars__list">
                  {pillar.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="hx-pillars__rail" role="group" aria-label="Platform pillars">
            {PILLARS.map((pillar, index) => (
              <button
                key={pillar.key}
                type="button"
                aria-current={index === activeIndex}
                className={`hx-pillars__rail-item ${index === activeIndex ? 'is-active' : ''}`}
                onClick={() => jumpToPillar(index)}
              >
                <span className="hx-pillars__rail-bar" aria-hidden="true" />
                {pillar.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
